import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { FocusEvent, KeyboardEvent, MouseEvent, PointerEvent as ReactPointerEvent } from 'react'

const SETTLE_MS = 200
const FLIP_MS = 220
const FALLBACK_MS = 600
const EDGE_PX = 70
const MAX_SCROLL_STEP = 28
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

type Options = {
  keys: readonly string[]
  onReorder: (keys: string[]) => void
  disabled?: boolean
}

type HandleProps = {
  type: 'button'
  disabled: boolean
  'aria-label': string
  'aria-pressed': boolean
  'data-sortable-handle': string
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  onBlur: (event: FocusEvent<HTMLElement>) => void
  onContextMenu: (event: MouseEvent<HTMLElement>) => void
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function itemsOf(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(':scope > [data-sortable-key]'))
}

function keyOf(el: HTMLElement): string {
  return el.dataset.sortableKey ?? ''
}

function moved<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list]
  const [item] = next.splice(from, 1)
  if (item === undefined) return next
  next.splice(to, 0, item)
  return next
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
  }
  return null
}

function resetItems(container: HTMLElement | null) {
  document.body.removeAttribute('data-sortable-active')
  if (!container) return
  container.querySelectorAll(':scope > [data-sortable-placeholder]').forEach((el) => el.remove())
  for (const el of itemsOf(container)) {
    el.style.transform = ''
    el.style.transition = ''
    el.removeAttribute('data-sortable-state')
  }
}

function playFlip(items: HTMLElement[], before: Map<string, number>) {
  if (prefersReducedMotion()) return
  const shifted: HTMLElement[] = []
  for (const el of items) {
    const was = before.get(keyOf(el))
    if (was === undefined) continue
    const delta = was - el.getBoundingClientRect().top
    if (Math.abs(delta) < 1) continue
    el.style.transition = 'none'
    el.style.transform = `translateY(${delta}px)`
    shifted.push(el)
  }
  if (shifted.length === 0) return
  void document.body.offsetHeight
  for (const el of shifted) {
    el.style.transition = `transform ${FLIP_MS}ms ${EASE}`
    el.style.transform = ''
  }
  window.setTimeout(() => {
    for (const el of shifted) el.style.transition = ''
  }, FLIP_MS + 40)
}

export function useSortable({ keys, onReorder, disabled = false }: Options) {
  const containerRef = useRef<HTMLDivElement>(null)
  const latest = useRef({ keys, onReorder })
  const cancel = useRef<(() => void) | null>(null)
  const flipFrom = useRef<Map<string, number> | null>(null)
  const grabbed = useRef<string | null>(null)
  const fallback = useRef<number | undefined>(undefined)
  const signature = keys.join('\u0000')

  useLayoutEffect(() => {
    latest.current = { keys, onReorder }
  })

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    window.clearTimeout(fallback.current)
    if (cancel.current && grabbed.current === null) return
    const items = itemsOf(container)
    const before = flipFrom.current
    flipFrom.current = null
    resetItems(container)
    if (before) playFlip(items, before)
    const key = grabbed.current
    if (key !== null) {
      const handle = container.querySelector<HTMLElement>(`[data-sortable-handle="${CSS.escape(key)}"]`)
      handle?.focus()
      handle?.setAttribute('aria-pressed', 'true')
    }
  }, [signature])

  useEffect(
    () => () => {
      window.clearTimeout(fallback.current)
      cancel.current?.()
    },
    [],
  )

  const startDrag = useCallback((key: string, event: ReactPointerEvent<HTMLElement>) => {
    const container = containerRef.current
    const handle = event.currentTarget
    if (!container || cancel.current) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.preventDefault()
    const items = itemsOf(container)
    const from = items.findIndex((el) => keyOf(el) === key)
    const el = items[from]
    const first = items[0]
    const last = items[items.length - 1]
    if (!el || !first || !last || items.length < 2) return

    const domKeys = items.map(keyOf)
    const tops = items.map((item) => item.offsetTop)
    const heights = items.map((item) => item.offsetHeight)
    const mids = tops.map((top, i) => top + (heights[i] ?? 0) / 2)
    const h = heights[from] ?? 0
    const gap = (tops[1] ?? 0) - (tops[0] ?? 0) - (heights[0] ?? 0)
    const step = h + gap
    const scroller = scrollParent(container)
    const scrollPos = () => (scroller ? scroller.scrollTop : window.scrollY)
    const scrollBy = (delta: number) => (scroller ? scroller.scrollBy(0, delta) : window.scrollBy(0, delta))
    const viewport = () => (scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight })
    const startY = event.clientY
    const startScroll = scrollPos()
    const minDy = (tops[0] ?? 0) - (tops[from] ?? 0)
    const maxDy = (tops[items.length - 1] ?? 0) + (heights[items.length - 1] ?? 0) - h - (tops[from] ?? 0)
    const reduce = prefersReducedMotion()
    let lastY = startY
    let to = from
    let raf = 0
    let ended = false
    let settle = 0

    const slot = document.createElement('div')
    slot.setAttribute('data-sortable-placeholder', '')
    slot.setAttribute('aria-hidden', 'true')
    slot.style.top = `${tops[from] ?? 0}px`
    slot.style.height = `${h}px`
    container.appendChild(slot)
    const slotTop = (index: number) => (index > from ? (tops[index] ?? 0) + (heights[index] ?? 0) - h : (tops[index] ?? 0))

    handle.setPointerCapture(event.pointerId)
    document.body.setAttribute('data-sortable-active', '')
    el.setAttribute('data-sortable-state', 'drag')
    for (const item of items) if (item !== el) item.setAttribute('data-sortable-state', 'shift')

    const place = () => {
      const dy = Math.min(maxDy, Math.max(minDy, lastY - startY + scrollPos() - startScroll))
      el.style.transform = `translateY(${dy}px)`
      const center = (tops[from] ?? 0) + dy + h / 2
      let target = 0
      for (let j = 0; j < items.length; j++) if (j !== from && (mids[j] ?? 0) < center) target++
      if (target === to) return
      to = target
      slot.style.transform = `translateY(${slotTop(to) - (tops[from] ?? 0)}px)`
      items.forEach((item, j) => {
        if (j === from) return
        item.style.transform = j > from && j <= to ? `translateY(${-step}px)` : j < from && j >= to ? `translateY(${step}px)` : ''
      })
    }

    const tick = () => {
      if (ended) return
      const view = viewport()
      const box = container.getBoundingClientRect()
      if (lastY < view.top + EDGE_PX && box.top < view.top) scrollBy(-Math.min(MAX_SCROLL_STEP, Math.ceil((view.top + EDGE_PX - lastY) / 5)))
      else if (lastY > view.bottom - EDGE_PX && box.bottom > view.bottom) scrollBy(Math.min(MAX_SCROLL_STEP, Math.ceil((lastY - (view.bottom - EDGE_PX)) / 5)))
      place()
      raf = requestAnimationFrame(tick)
    }

    const onMove = (move: PointerEvent) => {
      lastY = move.clientY
    }

    const detach = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onCancel)
      window.removeEventListener('keydown', onKey, true)
    }

    const finish = (commit: boolean) => {
      if (ended) return
      ended = true
      cancelAnimationFrame(raf)
      detach()
      const target = commit ? to : from
      el.setAttribute('data-sortable-state', 'drop')
      el.style.transform = `translateY(${slotTop(target) - (tops[from] ?? 0)}px)`
      slot.style.transform = `translateY(${slotTop(target) - (tops[from] ?? 0)}px)`
      if (target === from) for (const item of items) if (item !== el) item.style.transform = ''
      settle = window.setTimeout(
        () => {
          cancel.current = null
          if (target === from) {
            resetItems(container)
            return
          }
          fallback.current = window.setTimeout(() => resetItems(container), FALLBACK_MS)
          latest.current.onReorder(moved(domKeys, from, target))
        },
        reduce ? 0 : SETTLE_MS + 10,
      )
      cancel.current = () => {
        window.clearTimeout(settle)
        cancel.current = null
        resetItems(container)
      }
    }

    function onUp() {
      finish(true)
    }
    function onCancel() {
      finish(false)
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') finish(false)
    }

    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onCancel)
    window.addEventListener('keydown', onKey, true)
    cancel.current = () => {
      ended = true
      cancelAnimationFrame(raf)
      detach()
      window.clearTimeout(settle)
      cancel.current = null
      resetItems(container)
    }
    raf = requestAnimationFrame(tick)
  }, [])

  const handleKey = useCallback((key: string, event: KeyboardEvent<HTMLElement>) => {
    const container = containerRef.current
    const handle = event.currentTarget
    if (!container) return
    const isGrabbed = grabbed.current === key
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      grabbed.current = isGrabbed ? null : key
      handle.setAttribute('aria-pressed', String(!isGrabbed))
      return
    }
    if (!isGrabbed) return
    if (event.key === 'Escape') {
      grabbed.current = null
      handle.setAttribute('aria-pressed', 'false')
      return
    }
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    event.preventDefault()
    const items = itemsOf(container)
    const domKeys = items.map(keyOf)
    const from = domKeys.indexOf(key)
    const target = from + (event.key === 'ArrowUp' ? -1 : 1)
    if (from < 0 || target < 0 || target >= domKeys.length) return
    flipFrom.current = new Map(items.map((item) => [keyOf(item), item.getBoundingClientRect().top]))
    latest.current.onReorder(moved(domKeys, from, target))
  }, [])

  const handleBlur = useCallback((key: string, event: FocusEvent<HTMLElement>) => {
    if (grabbed.current !== key || event.relatedTarget === null) return
    grabbed.current = null
    event.currentTarget.setAttribute('aria-pressed', 'false')
  }, [])

  const handleProps = useCallback(
    (key: string, label: string): HandleProps => ({
      type: 'button',
      disabled,
      'aria-label': label,
      'aria-pressed': false,
      'data-sortable-handle': key,
      onPointerDown: (event) => startDrag(key, event),
      onKeyDown: (event) => handleKey(key, event),
      onBlur: (event) => handleBlur(key, event),
      onContextMenu: (event) => event.preventDefault(),
    }),
    [disabled, startDrag, handleKey, handleBlur],
  )

  const itemProps = useCallback((key: string) => ({ 'data-sortable-key': key }), [])

  return { containerRef, containerProps: { ref: containerRef, 'data-sortable-list': '' }, itemProps, handleProps }
}
